import { STANDARDS_EDITION_NOTE } from "@/lovable-demo/lib/warranty/standards";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { KpiCard, Panel, VerdictBadge } from "@/lovable-demo/components/bits";
import { cn } from "@/lib/utils";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";
import type { ClauseMapping, ProblemRecord } from "@/lovable-demo/lib/warranty/types";

export const Route = createFileRoute("/demo/compliance")({
  head: () => ({
    meta: [
      { title: "IATF 16949 & ISO 9001 Compliance — Broadview Warranty" },
      {
        name: "description",
        content:
          "Clause-level IATF 16949:2016 and ISO 9001 (2015→2026 transition) conformity view across every governed warranty problem record.",
      },
      { property: "og:title", content: "IATF 16949 & ISO 9001 Compliance — Broadview Warranty" },
      {
        property: "og:description",
        content:
          "Clause-level IATF 16949:2016 and ISO 9001 (2015→2026 transition) conformity view across every governed warranty problem record.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Compliance,
});

type Finding = ClauseMapping & { record: ProblemRecord };

const STANDARDS = ["IATF 16949:2016", "ISO 9001:2015"] as const;
type StandardFilter = "all" | (typeof STANDARDS)[number];

function Compliance() {
  const { data } = useWarranty();
  const [standard, setStandard] = useState<StandardFilter>("all");
  const [onlyIssues, setOnlyIssues] = useState(true);

  const findings = useMemo<Finding[]>(
    () => data.records.flatMap((r) => r.clauses.map((c) => ({ ...c, record: r }))),
    [data.records],
  );

  const scoped = findings.filter(
    (f) =>
      (standard === "all" || f.standard === standard) && (!onlyIssues || f.verdict !== "Compliant"),
  );

  const violated = findings.filter((f) => f.verdict === "Violated");
  const gaps = findings.filter((f) => f.verdict === "Gap");
  const compliant = findings.filter((f) => f.verdict === "Compliant");
  const recordsAtRisk = new Set(
    findings.filter((f) => f.verdict !== "Compliant").map((f) => f.record.id),
  ).size;
  const conformity = Math.round((compliant.length / Math.max(findings.length, 1)) * 100);

  const clauseRollup = useMemo(() => {
    const map = new Map<
      string,
      {
        standard: string;
        clause: string;
        title: string;
        violated: number;
        gap: number;
        compliant: number;
        records: Set<string>;
      }
    >();
    for (const f of findings) {
      if (standard !== "all" && f.standard !== standard) continue;
      const key = `${f.standard}::${f.clause}`;
      const row = map.get(key) ?? {
        standard: f.standard,
        clause: f.clause,
        title: f.title,
        violated: 0,
        gap: 0,
        compliant: 0,
        records: new Set<string>(),
      };
      if (f.verdict === "Violated") row.violated += 1;
      else if (f.verdict === "Gap") row.gap += 1;
      else row.compliant += 1;
      row.records.add(f.record.id);
      map.set(key, row);
    }
    return [...map.values()].sort(
      (a, b) =>
        b.violated * 10 + b.gap - (a.violated * 10 + a.gap) || a.clause.localeCompare(b.clause),
    );
  }, [findings, standard]);

  return (
    <AppShell
      title="IATF 16949 & ISO 9001"
      subtitle="Clause-level conformity evidence drawn from every governed problem record"
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Clause conformity"
          value={`${conformity}%`}
          tone={conformity >= 80 ? "good" : conformity >= 60 ? "warn" : "bad"}
          hint={`${compliant.length} of ${findings.length} clause assessments compliant`}
        />
        <KpiCard
          label="Violations"
          value={String(violated.length)}
          tone="bad"
          hint="Requirement not met — corrective action required"
        />
        <KpiCard
          label="Gaps"
          value={String(gaps.length)}
          tone="warn"
          hint="Partially satisfied — evidence incomplete"
        />
        <KpiCard
          label="Records with findings"
          value={String(recordsAtRisk)}
          tone="warn"
          hint={`of ${data.records.length} governed records across all programs`}
        />
      </div>

      <p className="mt-4 rounded border border-border bg-elevated/40 p-3 text-xs text-muted-foreground">
        {STANDARDS_EDITION_NOTE}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {(["all", ...STANDARDS] as StandardFilter[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStandard(s)}
            className={cn(
              "rounded border px-2.5 py-1 text-xs transition-colors",
              standard === s
                ? "border-primary/50 bg-primary/15 text-primary"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {s === "all" ? "Both standards" : s}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setOnlyIssues((v) => !v)}
          className={cn(
            "ml-auto rounded border px-2.5 py-1 text-xs transition-colors",
            onlyIssues
              ? "border-warning/50 bg-warning/12 text-warning"
              : "border-border text-muted-foreground hover:text-foreground",
          )}
        >
          {onlyIssues ? "Showing findings only" : "Showing all assessments"}
        </button>
      </div>

      <div className="mt-4">
        <Panel
          title="Clause rollup"
          subtitle="Which requirements the warranty loop is failing most often"
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Clause</th>
                  <th className="px-2 py-2 text-left font-medium">Standard</th>
                  <th className="px-2 py-2 text-right font-medium">Violated</th>
                  <th className="px-2 py-2 text-right font-medium">Gap</th>
                  <th className="px-2 py-2 text-right font-medium">Compliant</th>
                  <th className="px-2 py-2 text-right font-medium">Records</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {clauseRollup.map((row) => (
                  <tr key={`${row.standard}-${row.clause}`} className="hover:bg-elevated/60">
                    <td className="px-2 py-2.5">
                      <span className="num block text-xs text-primary">{row.clause}</span>
                      <span className="block">{row.title}</span>
                    </td>
                    <td className="px-2 py-2.5 text-xs text-muted-foreground">{row.standard}</td>
                    <td className="num px-2 py-2.5 text-right text-destructive">
                      {row.violated || "—"}
                    </td>
                    <td className="num px-2 py-2.5 text-right text-warning">{row.gap || "—"}</td>
                    <td className="num px-2 py-2.5 text-right text-success">
                      {row.compliant || "—"}
                    </td>
                    <td className="num px-2 py-2.5 text-right">{row.records.size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <div className="mt-4">
        <Panel
          title="Clause findings by record"
          subtitle="Rationale and the workflow stage that holds the evidence"
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Record</th>
                  <th className="px-2 py-2 text-left font-medium">Clause</th>
                  <th className="px-2 py-2 text-left font-medium">Verdict</th>
                  <th className="px-2 py-2 text-left font-medium">Rationale</th>
                  <th className="px-2 py-2 text-left font-medium">Linked stage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {scoped.map((f) => (
                  <tr key={`${f.record.id}-${f.id}`} className="hover:bg-elevated/60">
                    <td className="px-2 py-2.5">
                      <Link
                        to="/demo/problem-record/$id"
                        params={{ id: f.record.id }}
                        className="hover:text-primary"
                      >
                        <span className="num block text-xs text-primary">{f.record.code}</span>
                        <span className="block max-w-[200px] truncate">{f.record.component}</span>
                      </Link>
                    </td>
                    <td className="px-2 py-2.5">
                      <span className="num block text-xs">{f.clause}</span>
                      <span className="block text-xs text-muted-foreground">{f.title}</span>
                      <span className="block text-[0.68rem] text-muted-foreground/80">
                        {f.standard}
                      </span>
                    </td>
                    <td className="px-2 py-2.5">
                      <VerdictBadge verdict={f.verdict} />
                    </td>
                    <td className="max-w-[360px] px-2 py-2.5 text-xs text-muted-foreground">
                      {f.rationale}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-muted-foreground">{f.linkedStage}</td>
                  </tr>
                ))}
                {scoped.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-2 py-6 text-center text-muted-foreground">
                      No clause findings in this view.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Clause verdicts reflect the governed record content. They support internal audit preparation
        and are not a certification decision.
      </p>
    </AppShell>
  );
}
